
import { Component, Vue } from 'vue-property-decorator';
import { Scene, Sprite, Label, Group, Rect, Path, Triangle } from 'spritejs';
import { queryStructureChart } from '@/api/deal.js';
import { querySimple } from '@/api/deal.js';
import { throttle, labelWidth, labelHeight, spaceX, spaceY, mainLabelWidth, width, height, amountMap, colorMap, calWidth, wrapWord, legendArray } from './util';

interface chartNode {
  name: string;
  amountText: string;
  amount: string;
  radio?: string;
  children?: chartNode[];
  typeId: number; //  1主基金 2专项基金 3SPV 4项目 5LP
  percent?: string;
  spread?: boolean;
  width: number;
}
interface chartData {
  name?: string;
  investeds?: chartNode[];
  invests?: chartNode[];
}

@Component
class fundChart extends Vue {
  chartData: chartData = {};
  ratio: number = 1;
  scene: Scene | null = null;
  layer: any = null;
  totalGroup: any = null;
  isDraging: boolean = false;
  isClick: boolean = false;
  translate: number[] = [0, 0];
  spreadListA;
  pos: number[] = [0, 0];
  isShort: boolean = true;
  optionList: any[] = [];
  keyword: string = '';
  pointer: boolean = false;
  async mounted() {
    try {
      this.initSprite();
      await this.fetchData();
      this.draw();
    } catch (e) {}
  }

  async fetchData() {
    let { id, type } = this.$route.query;
    let res = await queryStructureChart({
      id,
      type
    });
    this.chartData = res.data;
  }

  initSprite() {
    const container = document.getElementById('stage');
    const scene = new Scene({
      container,
      width: width * this.ratio,
      height: height * this.ratio,
      stickyMode: 'scale',
      border: [4, '#000000']
    });
    const layer = scene.layer();
    layer.attr({ bdcolor: 'rgba(256,0,0,0)' });
    this.scene = scene;
    this.layer = layer;

    layer.addEventListener('beforerender', e => {
      //防止视窗大小重置比例
      scene.setResolution({ width: width * this.ratio, height: height * this.ratio });
    });

    // 拖拽
    let pos: number[] = [];
    scene.addEventListener('mousedown', e => {
      this.isClick = true;
      this.isDraging = true;
      pos = [e.x, e.y];
    });
    scene.addEventListener('mouseup', e => {
      this.isDraging = false;
    });
    scene.addEventListener(
      'mousemove',
      throttle(evt => {
        if (this.isDraging) {
          let { x, y } = evt;
          let translateX: number = this.translate[0] + x - pos[0];
          let translateY: number = this.translate[1] + y - pos[1];
          pos = [x, y];
          this.translate = [translateX, translateY];
          this.totalGroup.attr({
            translate: [translateX, translateY]
          });
        }
        this.isClick = false;
      }, 100)
    );
  }

  calculate() {
    this.chartData.invests && this.chartData.invests.forEach(node => calWidth(node));
    this.chartData.investeds && this.chartData.investeds.forEach(node => calWidth(node));
  }

  draw() {
    this.calculate(); //计算节点宽度
    let { invests, investeds } = this.chartData;
    let totalGroup = new Group({
      pos: [0, 0]
    });
    let targetFundNode = this.createNode(this.chartData, [0.5 * width, 0.5 * height], true, true);
    totalGroup.append(targetFundNode);
    if (investeds && investeds.length) {
      let fromTree = this.createTree(investeds, true, [0.5 * width, 0.5 * height - 0.5 * labelHeight]);
      totalGroup.append(fromTree);
    }
    if (invests && invests.length) {
      let toTree = this.createTree(invests, false, [0.5 * width, 0.5 * height + 0.5 * labelHeight]);
      totalGroup.append(toTree);
    }
    let [x, y] = this.translate;
    totalGroup.attr({
      translate: [x, y]
    });
    this.layer.append(totalGroup);
    this.totalGroup = totalGroup;
  }

  clear() {
    this.totalGroup.remove();
  }

  createTree(children: chartNode[] = [], directionUp, absPos: number[]) {
    let tree = new Group({
      pos: absPos
    });
    const length = children.length;
    const lineWidth =
      children.length > 1
        ? (length - 1) * spaceX +
          children.reduce((width, item, index) => {
            if (index === 0 || index === children.length - 1) {
              return width + 0.5 * item.width;
            } else {
              return width + item.width;
            }
          }, 0)
        : 0;

    // 确定竖线和子节点定位
    let leftPos = 0;
    let posList = children.map((item, index) => {
      if (index !== 0) {
        leftPos = leftPos + 0.5 * item.width;
      }
      let pos = [
        [-0.5 * lineWidth + leftPos, directionUp ? -spaceY : spaceY],
        [-0.5 * lineWidth + leftPos, directionUp ? -2 * spaceY : 2 * spaceY]
      ];
      leftPos = 0.5 * item.width + leftPos + spaceX;
      return pos;
    });
    tree.append(this.createLine(posList, directionUp, lineWidth));
    tree.append(...this.createArrow(posList, directionUp));
    tree.append(...this.createPercent(posList, children));
    let labels = children.map((node, index) => {
      let pos = posList[index][1];
      pos[1] = pos[1] + (directionUp ? -0.5 * labelHeight : 0.5 * labelHeight);
      let nodeAbsPos = [absPos[0] + pos[0], absPos[1] + pos[1]];
      let label = this.createNode(node, pos, directionUp);
      return label;
    });
    tree.append(...labels);
    return tree;
  }

  createLine(posList, directionUp, lineWidth) {
    posList = posList.map(item => item);
    posList.push([
      [0, 0],
      [0, directionUp ? -spaceY : spaceY]
    ]);
    let path = posList.reduce((path, pos) => {
      pos = pos.map(item => item.join(','));
      return path + `M${pos[0]}L${pos[1]}`;
    }, `M${-0.5 * lineWidth}, ${directionUp ? -spaceY : spaceY}L${0.5 * lineWidth}, ${directionUp ? -spaceY : spaceY}`);
    return new Path({
      d: path,
      strokeColor: 'rgb(164,161,161)',
      fillColor: 'rgb(164,161,161)',
      lineWidth: 3
    });
  }

  createArrow(posList, directionUp) {
    if (posList.length === 1) {
      let tri = new Triangle({
        pos: [posList[0][0][0] - 10, posList[0][0][1]],
        sides: [20, 20],
        anchor: [0.5, 0.5],
        fillColor: 'rgb(164,161,161)'
      });
      return [tri];
    } else {
      return posList.map(
        item =>
          new Triangle({
            pos: [0.5 * (item[0][0] + item[1][0]) - 10, 0.5 * (item[0][1] + item[1][1])],
            sides: [20, 20],
            anchor: [0.5, 0.5],
            fillColor: 'rgb(164,161,161)'
          })
      );
    }
  }

  createPercent(posList, children) {
    return children.reduce((list, item, index) => {
      if (item.percent && item.auth) {
        let pos = posList[index];
        list.push(
          new Label({
            text: item.percent,
            size: [200, 40],
            pos: [0.5 * (pos[0][0] + pos[1][0]) + 20, 0.5 * (pos[0][1] + pos[1][1])],
            fillColor: 'rgb(99,165,251)',
            fontSize: 25,
            textAlign: 'left'
          })
        );
      }
      return list;
    }, []);
  }

  createNode(chartNode, pos, directionUp?: boolean, isTarget?: boolean) {
    let { children, spread, detailTypeId, auth } = chartNode;
    const color = colorMap[detailTypeId] || colorMap.def;
    //节点整体，包含主体方块、子树、展开按钮、提示方块
    let group = new Group({
      size: [isTarget ? mainLabelWidth : labelWidth, labelHeight],
      anchor: [0.5, 0.5],
      pos,
      zIndex: 1
    });

    let boxGroup = this.createBox(chartNode, isTarget, directionUp); //主体方块
    let tipCroup = this.createTips(chartNode, isTarget, directionUp); //提示方块
    group.append(boxGroup);
    boxGroup.addEventListener('mouseenter', evt => {
      auth && group.append(tipCroup);
      boxGroup.attr({ border: [4, color[3]] });
    });
    boxGroup.addEventListener('mouseleave', evt => {
      auth && group.removeChild(tipCroup);
      boxGroup.attr({ border: [2.5, color[2]] });
    });
    group.addEventListener('mouseenter', evt => {
      this.pointer = true;
    });
    group.addEventListener('mouseleave', evt => {
      this.pointer = false;
    });

    if (children && children.length) {
      let spreadBtn = this.createSpreadBtn(chartNode, directionUp); //展开按钮
      if (spread) {
        //子树
        let childTree = this.createTree(children, directionUp, [0, directionUp ? -0.5 * labelHeight - 40 : 0.5 * labelHeight + 40]);
        group.append(childTree);
        spreadBtn.attr({
          text: '—'
        });
      }
      group.append(spreadBtn);
    }
    return group;
  }

  createBox(chartNode, isTarget, directionUp) {
    let { shortName, name, detailTypeId, amount, children, spread, amount2, auth, detailTypeName } = chartNode;
    const color = colorMap[detailTypeId] || colorMap.def;
    let nameText, amountText;
    let width = isTarget ? mainLabelWidth : labelWidth;
    let boxGroup = new Group({
      size: [width, labelHeight],
      anchor: [0.5, 0.5],
      bgcolor: color[1],
      border: [2.5, color[2]]
    });
    let nameLabels = this.createNameLabel(chartNode, width, color);
    boxGroup.append(...nameLabels);
    if (auth) {
      let detailLabel = this.createAmountLabel(chartNode, isTarget, color, directionUp);
      boxGroup.append(detailLabel);
    }
    boxGroup.addEventListener('click', () => {
      if (!this.isClick) {
        return;
      }
      if (!auth) {
        this.$message.warn('无权限');
        return;
      }
      let { id, typeId } = chartNode;
      window.open(`${window.location.origin}/finance/fundManage/fundChart?type=${typeId}&id=${id}`);
    });
    return boxGroup;
  }

  createNameLabel(chartNode, width, color) {
    let { isShort } = this;
    let { name, shortName, auth, detailTypeName } = chartNode;
    let nameText;
    if (auth) {
      nameText = isShort ? shortName || name || '' : name || '';
    } else {
      nameText = detailTypeName || '';
    }
    const nameArr = wrapWord(nameText, width);
    let nameLabels = nameArr.map((name, index) => {
      return new Label({
        text: name,
        anchor: [0.5, 0.5],
        width,
        pos: [0, nameArr.length === 1 ? -0.18 * labelHeight : -0.3 * (1 - index) * labelHeight],
        fillColor: color[0],
        lineHeight: 0.5 * labelHeight,
        fontSize: 32,
        textAlign: nameArr.length === 1 ? 'center' : 'left',
        vertical: 'center',
        paddingLeft: nameArr.length === 1 ? 0 : 30
      });
    });
    return nameLabels;
  }

  createAmountLabel(chartNode, isTarget, color, directionUp) {
    return new Label({
      text: this.getAmountText(chartNode, isTarget, directionUp),
      anchor: [0.5, 0.5],
      pos: [0, 0.3 * labelHeight],
      fillColor: color[0],
      lineHeight: 0.4 * labelHeight,
      fontSize: 25,
      textAlign: 'center',
      vertical: 'center'
    });
  }

  createSpreadBtn(chartNode, directionUp) {
    let { children, spread, detailTypeId } = chartNode;
    let spreadBtn = new Label({
      text: '+',
      anchor: [0.5, directionUp ? 1 : 0],
      size: [40, 40],
      pos: [0, directionUp ? -0.5 * labelHeight : 0.5 * labelHeight],
      fillColor: '#fff',
      bgcolor: 'rgb(171,207,231)',
      fontSize: 30,
      textAlign: 'center',
      borderRadius: 20,
      fontWeight: 'bold'
    });
    spreadBtn.addEventListener('click', evt => {
      chartNode.spread = !spread;
      this.clear();
      this.draw();
    });
    return spreadBtn;
  }

  getAmountText(chartNode, isTarget, directionUp) {
    let { detailTypeId, amount, scale } = chartNode;
    let amountName;
    if (isTarget) {
      if (detailTypeId === 1 || detailTypeId === 2 || detailTypeId === 3) {
        amountName = '已投金额';
      } else if (detailTypeId === 4) {
        amountName = '被投金额';
      } else {
        amountName = '认缴金额';
      }
    } else {
      amountName = directionUp ? '认缴金额' : '被投金额';
    }
    let amountText = `${amountName}：${amount || ''}`;
    if (detailTypeId === 1 || detailTypeId === 2 || detailTypeId === 3) {
      amountText = `规模：${scale || ''}     ${amountText}`;
    }
    return amountText;
  }

  createTips(node, isTarget, directionUp) {
    let lineHeight = 35;
    let width = 700;
    let group = new Group({
      size: [width, lineHeight * 4],
      anchor: [0, 1],
      bgcolor: 'rgba(92,92,92, 0.8)',
      pos: [0, -60],
      borderRadius: 10,
      zIndex: 999
    });
    let tipList = [
      `类别： ${node.detailTypeName || ''}`,
      `简称： ${node.shortName || ''}`,
      `全称： ${node.name || ''}`,
      this.getAmountText(node, isTarget, directionUp)
    ];
    let labelList = tipList.map(
      (text, index) =>
        new Label({
          text,
          anchor: [0, 1],
          pos: [20, (index - 3) * lineHeight],
          fillColor: '#ffffff',
          lineHeight,
          fontSize: 23,
          textAlign: 'left',
          vertical: 'center'
        })
    );
    group.append(...labelList);
    return group;
  }

  zoomIn() {
    if (this.ratio <= 0.5) {
      return;
    }
    this.ratio /= 1.1;
    this.scene && this.scene.setResolution({ width: width * this.ratio, height: height * this.ratio });
  }
  zoomOut() {
    if (this.ratio >= 2) {
      return;
    }
    this.ratio *= 1.1;
    this.scene && this.scene.setResolution({ width: width * this.ratio, height: height * this.ratio });
  }

  handleInput(val) {
    this.keyword = val;
    this.search();
  }

  search() {
    let { keyword } = this;
    keyword = keyword.trim();
    if (keyword) {
      querySimple({
        type: 0,
        name: keyword
      })
        .then(res => {
          let { data } = res || {};
          if (data && data.length) {
            this.optionList = data;
          } else {
            this.clearList();
          }
        })
        .catch(e => {
          this.clearList();
        });
    }
  }

  clearList() {
    this.optionList = [];
  }
  onSelect(val) {
    let selectedItem = this.optionList.find(item => item.name === val);
    let { type, id } = selectedItem;
    window.open(`${window.location.origin}/finance/fundManage/fundChart?type=${type}&id=${id}`);
  }
  switchName() {
    this.isShort = !this.isShort;
    this.clear();
    this.draw();
  }
  render() {
    let { chartData, optionList, isShort, pointer } = this;
    return (
      <div class="page-container">
        <div class="title-bar">
          <span>{chartData.name}</span>

          {isShort ? (
            <div class="label-container" onClick={this.switchName}>
              <a-icon type="eye-invisible" theme="twoTone" style="font-size: 25px;" />
              <span class="text-btn">显示简称</span>
            </div>
          ) : (
            <div class="label-container" onClick={this.switchName}>
              <a-icon type="eye" theme="twoTone" style="font-size: 25px;" />
              <span class="text-btn">显示全称</span>
            </div>
          )}
          <div class="certain-category-search-wrapper" style="width: 300px">
            <a-auto-complete
              class="certain-category-search"
              dropdown-class-name="certain-category-search-dropdown"
              dropdown-match-select-width={false}
              size="large"
              style="width: 100%"
              placeholder="搜索基金/项目/LP"
              option-label-prop="value"
              onSearch={this.handleInput}
              onSelect={this.onSelect}
            >
              <template slot="dataSource">
                {optionList.map(item => (
                  <a-select-option key={item.name} title={item.name} data-item={item}>
                    {item.name}
                  </a-select-option>
                ))}
              </template>
              <a-input>
                <a-icon slot="suffix" type="search" class="certain-category-icon" />
              </a-input>
            </a-auto-complete>
          </div>
          <div></div>
        </div>
        <div class="legend">
          {legendArray.map(legend => {
            let style = `background: ${legend.color[1]};border: 2px solid ${legend.color[2]};`;
            return (
              <div class="legend-item">
                <div class="box" style={style}></div>
                <div class="name"> {legend.name}</div>
              </div>
            );
          })}
        </div>
        <div id="stage" class={pointer ? 'canvas-container pointer' : 'canvas-container'}></div>
        <div class="operate">
          <a-icon class="button" type="zoom-in" onClick={this.zoomIn} />
          <a-icon class="button" type="zoom-out" onClick={this.zoomOut} />
        </div>
      </div>
    );
  }
}

export default fundChart;
